import React, { useEffect, useState } from "react";
import { useApp } from "@/context/AppContext";
import { getSettings, adminUpdateSettings } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Loader2, Save, MessageCircle, Store, Phone, MapPin, Info, Mail, Clock, User, FileText, Map, CreditCard, Truck } from "lucide-react";
import UpiQRCode from "@/components/UpiQRCode";
import AdminCredentialsForm from "@/components/AdminCredentialsForm";
import { toast } from "sonner";

const AdminSettingsPage = () => {
  const { t, adminToken } = useApp();
  const [form, setForm] = useState({
    shop_name: "",
    shop_whatsapp: "",
    shop_phone: "",
    shop_email: "",
    shop_address: "",
    business_hours: "",
    owner_name: "",
    gst_number: "",
    maps_url: "",
    upi_id: "",
    upi_name: "",
    fertilizer_license: "",
    seed_license: "",
    pesticide_license: "",
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    getSettings()
      .then((s) => setForm({
        shop_name: s.shop_name || "",
        shop_whatsapp: s.shop_whatsapp || "",
        shop_phone: s.shop_phone || "",
        shop_email: s.shop_email || "",
        shop_address: s.shop_address || "",
        business_hours: s.business_hours || "",
        owner_name: s.owner_name || "",
        gst_number: s.gst_number || "",
        maps_url: s.maps_url || "",
        upi_id: s.upi_id || "",
        upi_name: s.upi_name || "",
        fertilizer_license: s.fertilizer_license || "",
        seed_license: s.seed_license || "",
        pesticide_license: s.pesticide_license || "",
        delivery_fee: s.delivery_fee ?? 40,
        free_delivery_threshold: s.free_delivery_threshold ?? 500,
      }))
      .finally(() => setLoading(false));
  }, []);

  const update = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...form,
        delivery_fee: parseFloat(form.delivery_fee) || 0,
        free_delivery_threshold: parseFloat(form.free_delivery_threshold) || 0,
      };
      await adminUpdateSettings(adminToken, payload);
      toast.success(t("settingsSaved"));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="h-40 bg-white border border-[#E2E8F0] rounded-md animate-pulse" />;
  }

  return (
    <div className="max-w-2xl" data-testid="admin-settings-page">
      <h1 className="font-heading font-bold text-2xl md:text-3xl text-[#1E293B]">{t("shopSettings")}</h1>
      <p className="text-sm text-slate-500 mt-1 mb-6">{t("settingsDesc")}</p>

      <form onSubmit={save} className="space-y-5 bg-white border border-[#E2E8F0] rounded-md p-5 md:p-6">
        <div>
          <Label className="text-sm font-medium flex items-center gap-1.5">
            <Store className="w-3.5 h-3.5" /> {t("shopName")}
          </Label>
          <Input value={form.shop_name} onChange={update("shop_name")} data-testid="settings-shop-name" className="mt-1.5 h-11 bg-white" />
        </div>

        <div>
          <Label className="text-sm font-medium flex items-center gap-1.5">
            <User className="w-3.5 h-3.5 text-[#2D5A27]" /> {t("ownerName")}
          </Label>
          <Input value={form.owner_name} onChange={update("owner_name")} data-testid="settings-owner-name" className="mt-1.5 h-11 bg-white" />
        </div>

        <div>
          <Label className="text-sm font-medium flex items-center gap-1.5">
            <FileText className="w-3.5 h-3.5 text-[#D97706]" /> {t("gstNumber")}
          </Label>
          <Input value={form.gst_number} onChange={update("gst_number")} data-testid="settings-gst" placeholder="e.g. 27DQAPP5396F1ZH" className="mt-1.5 h-11 bg-white font-mono uppercase" />
        </div>

        <div>
          <Label className="text-sm font-medium flex items-center gap-1.5">
            <MessageCircle className="w-3.5 h-3.5 text-green-600" /> {t("whatsappNumber")}
          </Label>
          <Input
            value={form.shop_whatsapp}
            onChange={update("shop_whatsapp")}
            placeholder="919876543210"
            data-testid="settings-whatsapp"
            className="mt-1.5 h-11 bg-white font-mono"
          />
          <div className="mt-2 p-3 rounded-md bg-emerald-50 border border-emerald-200 flex gap-2 text-xs text-emerald-900">
            <Info className="w-4 h-4 shrink-0 mt-0.5" />
            <p>{t("whatsappHelp")}</p>
          </div>
        </div>

        <div>
          <Label className="text-sm font-medium flex items-center gap-1.5">
            <Phone className="w-3.5 h-3.5" /> {t("shopPhone")}
          </Label>
          <Input value={form.shop_phone} onChange={update("shop_phone")} data-testid="settings-phone" className="mt-1.5 h-11 bg-white font-mono" />
        </div>

        <div>
          <Label className="text-sm font-medium flex items-center gap-1.5">
            <MapPin className="w-3.5 h-3.5" /> {t("shopAddress")}
          </Label>
          <Textarea value={form.shop_address} onChange={update("shop_address")} data-testid="settings-address" rows={2} className="mt-1.5 bg-white" />
        </div>

        <div>
          <Label className="text-sm font-medium flex items-center gap-1.5">
            <Map className="w-3.5 h-3.5 text-[#2D5A27]" /> {t("mapsUrl")}
          </Label>
          <Input
            type="url"
            value={form.maps_url}
            onChange={update("maps_url")}
            placeholder="https://maps.app.goo.gl/..."
            data-testid="settings-maps-url"
            className="mt-1.5 h-11 bg-white"
          />
        </div>

        <div>
          <Label className="text-sm font-medium flex items-center gap-1.5">
            <Clock className="w-3.5 h-3.5 text-[#2D5A27]" /> {t("businessHours")}
          </Label>
          <Input
            value={form.business_hours}
            onChange={update("business_hours")}
            placeholder={t("businessHoursHelp")}
            data-testid="settings-hours"
            className="mt-1.5 h-11 bg-white"
          />
          <p className="text-xs text-slate-500 mt-1.5">{t("businessHoursHelp")}</p>
        </div>

        <div className="pt-2 border-t border-[#E2E8F0]">
          <Button
            type="submit"
            disabled={saving}
            data-testid="save-settings-btn"
            className="rounded-md bg-[#2D5A27] hover:bg-[#21421D] text-white font-semibold"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : (<><Save className="w-4 h-4 mr-1.5" /> {t("saveSettings")}</>)}
          </Button>
        </div>
      </form>

      {/* UPI section */}
      <div className="mt-6 bg-white border border-[#E2E8F0] rounded-md p-5 md:p-6" data-testid="upi-settings-section">
        <div className="flex items-center gap-2 mb-4">
          <CreditCard className="w-4 h-4 text-[#D97706]" />
          <h2 className="font-heading font-semibold text-lg">UPI / Online Payment</h2>
        </div>
        <div className="grid md:grid-cols-2 gap-6 items-start">
          <div className="space-y-4">
            <div>
              <Label className="text-sm font-medium">UPI ID (Virtual Payment Address)</Label>
              <Input
                value={form.upi_id}
                onChange={update("upi_id")}
                placeholder="yourname@oksbi"
                data-testid="settings-upi-id"
                className="mt-1.5 h-11 bg-white font-mono"
              />
            </div>
            <div>
              <Label className="text-sm font-medium">Payee Name (shown while paying)</Label>
              <Input
                value={form.upi_name}
                onChange={update("upi_name")}
                placeholder="e.g. Prathmesh Patil"
                data-testid="settings-upi-name"
                className="mt-1.5 h-11 bg-white"
              />
            </div>
            <p className="text-xs text-slate-500">
              Customers will see this QR code on checkout to pay in advance from any UPI app (GPay, PhonePe, Paytm, BHIM).
            </p>
          </div>
          <div className="flex justify-center">
            {form.upi_id ? (
              <UpiQRCode upiId={form.upi_id} name={form.upi_name} size={180} />
            ) : (
              <div className="w-[200px] h-[200px] rounded-xl border-2 border-dashed border-[#E2E8F0] flex items-center justify-center text-slate-400 text-xs text-center p-4">
                Enter UPI ID to preview QR code
              </div>
            )}
          </div>
        </div>
      </div>
      {/* Delivery section */}
      <div className="mt-6 bg-white border border-[#E2E8F0] rounded-md p-5 md:p-6" data-testid="delivery-settings-section">
        <div className="flex items-center gap-2 mb-4">
          <Truck className="w-4 h-4 text-[#2D5A27]" />
          <h2 className="font-heading font-semibold text-lg">Delivery / डिलीवरी</h2>
        </div>
        <form onSubmit={save} className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label className="text-sm font-medium">Delivery Fee (₹) / डिलीवरी शुल्क</Label>
            <Input
              type="number"
              step="1"
              min="0"
              value={form.delivery_fee}
              onChange={update("delivery_fee")}
              data-testid="settings-delivery-fee"
              className="mt-1.5 h-11 bg-white"
            />
            <p className="text-xs text-slate-500 mt-1">
              हर order पर लगने वाला delivery शुल्क। <strong>0 करें</strong> तो सारे orders मुफ्त delivery के साथ आएँगे।
            </p>
          </div>
          <div>
            <Label className="text-sm font-medium">Free Delivery Above (₹) / मुफ्त डिलीवरी सीमा</Label>
            <Input
              type="number"
              step="1"
              min="0"
              value={form.free_delivery_threshold}
              onChange={update("free_delivery_threshold")}
              data-testid="settings-free-delivery-threshold"
              className="mt-1.5 h-11 bg-white"
            />
            <p className="text-xs text-slate-500 mt-1">
              इस amount से ऊपर के order पर delivery मुफ्त। <strong>0 करें</strong> तो free delivery option बंद हो जाएगा (हर order पर fee लगेगा)।
            </p>
          </div>
          <div className="md:col-span-2 p-3 rounded-md bg-emerald-50 border border-emerald-200 text-xs text-emerald-900">
            <strong>Current policy:</strong>{" "}
            {parseFloat(form.delivery_fee) <= 0
              ? "सारे orders पर FREE delivery"
              : parseFloat(form.free_delivery_threshold) <= 0
              ? `हर order पर ₹${form.delivery_fee} delivery fee (कोई free option नहीं)`
              : `₹${form.free_delivery_threshold} से ऊपर के order पर FREE, नहीं तो ₹${form.delivery_fee} fee`}
          </div>
          <div className="md:col-span-2 pt-2 border-t border-[#E2E8F0]">
            <Button
              type="submit"
              disabled={saving}
              data-testid="save-delivery-btn"
              className="rounded-md bg-[#2D5A27] hover:bg-[#21421D] text-white font-semibold"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : (<><Save className="w-4 h-4 mr-1.5" /> Save Delivery Settings</>)}
            </Button>
          </div>
        </form>
      </div>

      {/* Licenses section */}
      <div className="mt-6 bg-white border border-[#E2E8F0] rounded-md p-5 md:p-6" data-testid="license-settings-section">
        <div className="flex items-center gap-2 mb-4">
          <FileText className="w-4 h-4 text-[#2D5A27]" />
          <h2 className="font-heading font-semibold text-lg">Government Licenses</h2>
        </div>
        <p className="text-xs text-slate-500 mb-4">
          License numbers appear on printed invoices and footer for legal compliance.
        </p>
        <form
          onSubmit={save}
          className="grid grid-cols-1 md:grid-cols-3 gap-4"
        >
          <div>
            <Label className="text-sm font-medium">Fertilizer License</Label>
            <Input
              value={form.fertilizer_license}
              onChange={update("fertilizer_license")}
              placeholder="LCFRD..."
              data-testid="settings-fert-license"
              className="mt-1.5 h-11 bg-white font-mono uppercase text-sm"
            />
          </div>
          <div>
            <Label className="text-sm font-medium">Seed License</Label>
            <Input
              value={form.seed_license}
              onChange={update("seed_license")}
              placeholder="LCSD..."
              data-testid="settings-seed-license"
              className="mt-1.5 h-11 bg-white font-mono uppercase text-sm"
            />
          </div>
          <div>
            <Label className="text-sm font-medium">Pesticide/Insecticide License</Label>
            <Input
              value={form.pesticide_license}
              onChange={update("pesticide_license")}
              placeholder="LCID..."
              data-testid="settings-pest-license"
              className="mt-1.5 h-11 bg-white font-mono uppercase text-sm"
            />
          </div>
          <div className="md:col-span-3 pt-2 border-t border-[#E2E8F0]">
            <Button
              type="submit"
              disabled={saving}
              data-testid="save-licenses-btn"
              className="rounded-md bg-[#2D5A27] hover:bg-[#21421D] text-white font-semibold"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : (<><Save className="w-4 h-4 mr-1.5" /> Save Licenses</>)}
            </Button>
          </div>
        </form>
      </div>

      <AdminCredentialsForm />
    </div>
  );
};

export default AdminSettingsPage;
